import { difficultyFor, generateRoute, recipeById, recipeForSeed } from '../core/world/route-gen.js';
import { route as routeTunables } from '../core/tunables.js';
import type { RouteDef } from '../core/world/route.js';
import type { SaveData } from '../core/save/model.js';
import { missionFor } from './placeholder-content.js';

/**
 * The one place a route is grown from a mission and a seed.
 *
 * It exists because three screens need to agree about the same world and none of them can remember it
 * for the others. The briefing needs to know how many things are on the road, so it can tell the loop
 * state what a full load is. Both drive legs need the road itself. The dev overlay needs the count too,
 * or a forced drive gates on the wrong number.
 *
 * Each of them calling `generateRoute` with its own arguments is exactly how the number she is shown and
 * the number actually lying on the ground come to disagree - and that particular disagreement is the one
 * unrecoverable bug in the whole feature: too few items placed and the trailer never fills, so the road
 * home never opens and the only way out is the back paw. One function, one set of arguments, no drift.
 *
 * Cheap enough to call freely: it is a few hundred heightfield samples and no allocation worth counting.
 * It is also pure, so calling it twice for the same mission is guaranteed to give the same road.
 */
export function routeForMission(
  missionId: string | null,
  routeSeed: number,
  save: SaveData,
  options: { readonly freePlay?: boolean } = {},
): RouteDef {
  const mission = missionFor(missionId);
  /*
   * A mission names the kind of place; free play has no mission, so the seed names it. Falling through
   * to `meadow-lane` here meant every free-play drive was the same green lane however new the seed was.
   */
  const recipe =
    mission === null ? recipeForSeed(routeSeed, save.unlocks) : recipeById(mission.recipeId);
  return generateRoute({
    recipe,
    seed: routeSeed,
    // How many missions she has finished, so the ground gets gently more interesting as she improves.
    difficulty: difficultyFor(save.missionHistory.length),
    lengthM: options.freePlay === true ? routeTunables.freePlayLengthM : undefined,
  });
}
